import * as Api from '@ViewModels';

/**
 * Availability rules and publish-error copy for the "generate a unique meeting link" option on a virtual meeting
 * location. Kept out of the component so the `=== true` capability rule and the error mapping are unit-testable.
 *
 * Ref: https://github.com/Real-Magic/Levitate/issues/19669
 */

/**
 * The calendar provider can mint a per-booking link. `canGenerateUniqueMeetingLink` is absent (not null) when the API
 * could not determine it, so anything other than an explicit `true` means "do not offer" — see the field's doc comment.
 */
export const canGenerateCalendarUniqueLink = (calendarFeatures?: Api.IProviderCalendarFeatures) =>
	calendarFeatures?.canGenerateUniqueMeetingLink === true;

const CalendarOnlyUnavailableReason = `Your calendar connection can’t create a unique link for each booking. That needs a Microsoft Teams or Google Meet calendar connection.`;

const ZoomAvailableUnavailableReason = `Your calendar connection can’t create a unique link for each booking. Connect your Zoom account in settings to generate a unique Zoom link instead.`;

export interface IUniqueMeetingLinkAvailability {
	/** Whether any provider can mint a per-booking link, so the option may be offered. */
	available: boolean;
	/** Whether the calendar provider specifically can, which decides if the provider sub-choice is worth showing. */
	supportsCalendarDefault: boolean;
	/** Why the option is unavailable, for display next to the disabled control. Null when available or still resolving. */
	disabledReason: string | null;
}

/**
 * Which unique-link providers are usable by the meeting owner. The two paths are deliberately independent: Zoom creates
 * the meeting against the owner's own Zoom account and works on any calendar provider, so it must never be filtered by
 * the calendar capability.
 *
 * @param isResolving true while the capability inputs are still loading, so we suppress the reason rather than briefly
 * telling the user something that may not be true.
 */
export function getUniqueMeetingLinkAvailability({
	calendarFeatures,
	isResolving = false,
	isZoomConnected,
	isZoomFeatureEnabled,
}: {
	calendarFeatures?: Api.IProviderCalendarFeatures;
	isResolving?: boolean;
	isZoomConnected: boolean;
	isZoomFeatureEnabled: boolean;
}): IUniqueMeetingLinkAvailability {
	const supportsCalendarDefault = canGenerateCalendarUniqueLink(calendarFeatures);
	const available = supportsCalendarDefault || isZoomConnected;

	if (available || isResolving) {
		return { available, supportsCalendarDefault, disabledReason: null };
	}

	return {
		available,
		supportsCalendarDefault,
		disabledReason: isZoomFeatureEnabled ? ZoomAvailableUnavailableReason : CalendarOnlyUnavailableReason,
	};
}

/**
 * Publish-time validation messages the API returns for a virtual location that asks for a unique link nothing can mint,
 * mapped to copy that names the option at fault and the way out. Keyed by the API's exact `systemMessage`.
 *
 * These also fire for a virtual option inside an "allow invitee to decide" meeting type, which previously bypassed
 * validation — so a meeting type that saved before can now be rejected (it was producing bookings with no link).
 */
const UniqueLinkPublishErrors: Record<string, string> = {
	'Generating a unique meeting link requires an Office 365 or Google connection': `“Generate a unique meeting link” needs a Microsoft Teams or Google Meet calendar connection. Switch the link provider to Zoom, or turn the option off and enter a meeting link.`,
	'Generating a unique meeting link is not supported while Redtail is used for meeting scheduling': `“Generate a unique meeting link” isn’t supported while Redtail handles your meeting scheduling. Switch the link provider to Zoom, or turn the option off and enter a meeting link.`,
	'The Zoom integration is not enabled for this account': `A unique Zoom link isn’t available — the Zoom integration is not enabled for this account. Choose another link provider, or turn the option off and enter a meeting link.`,
	'Generating a unique Zoom link requires a connected Zoom account': `“Generate a unique Zoom link” needs a connected Zoom account. Connect Zoom in settings, or turn the option off and enter a meeting link.`,
};

/**
 * Maps a publish (commit) failure to inline copy for the meeting-location section, or null when the failure is
 * unrelated and belongs in the generic error surface.
 */
export function getUniqueMeetingLinkPublishError(systemMessage?: string): string | null {
	// Tolerate an added sentence-ending period so a punctuation tweak on the API side degrades to the generic error
	// rather than silently matching nothing.
	const normalized = systemMessage?.trim().replace(/\.$/, '') ?? '';
	return UniqueLinkPublishErrors[normalized] ?? null;
}
